// Backlog 073 — pre-upload preflight orchestration. Pure logic + injected
// IO (client, prompts, writer). The plug verb wires the real injectors;
// tests pass stubs.
//
// Voice: §23 — plain, specific, no exclamation marks.

import type { IV2Client } from '../api/v2-client.js';
import { V2ApiError } from '../api/v2-errors.js';
import { classifyKey, type ServiceEnvKeys } from './env-preflight.js';

export interface PreflightDeps {
  isTTY: boolean;
  /**
   * y/N confirm. `defaultValue` defaults to false. The plug verb wires
   * inquirer's `confirm` type; tests pass an array-driver.
   */
  confirm: (message: string, defaultValue?: boolean) => Promise<boolean>;
  /**
   * Free-form text input. `hidden: true` swaps to a masked prompt
   * (inquirer `password` type). Returning '' is interpreted as "skip".
   */
  askInput: (
    message: string,
    opts?: { hidden?: boolean },
  ) => Promise<string>;
  writer: (s: string) => void;
}

export interface MissingKey {
  service: string;
  key: string;
  classification: 'env' | 'secret';
}

export interface RunEnvPreflightOpts {
  client: IV2Client;
  entityId: string;
  declared: ReadonlyArray<ServiceEnvKeys>;
  forceMissingEnv: boolean;
  deps: PreflightDeps;
}

export type PreflightOutcome = { action: 'proceed' } | { action: 'abort' };

interface EnvListResponse {
  env: Array<{ key: string; value: string }>;
}
interface SecretsListResponse {
  secrets: Array<{ key: string }>;
}

/**
 * Detect missing keys, render warnings, and (in TTY mode) drive the set-now
 * + continue-anyway prompts. Returns `proceed` to let plug continue, or
 * `abort` to stop before upload. Never throws on user-cancel — the verb
 * handles the abort case as a clean non-zero exit.
 */
export async function runEnvPreflight(
  opts: RunEnvPreflightOpts,
): Promise<PreflightOutcome> {
  if (opts.declared.length === 0) return { action: 'proceed' };

  const known = await fetchKnownKeys(opts.client, opts.entityId);
  const missing = computeMissing(opts.declared, known);
  if (missing.length === 0) return { action: 'proceed' };

  printWarnings(opts.deps.writer, groupByService(missing));

  if (!opts.deps.isTTY) {
    if (opts.forceMissingEnv) return { action: 'proceed' };
    opts.deps.writer(
      'Re-run with --force-missing-env to deploy anyway in non-interactive mode.\n',
    );
    return { action: 'abort' };
  }

  // TTY: offer set-now first. Successful set-now (every key valued) removes
  // any reason to continue-anyway.
  const wantsSetNow = await opts.deps.confirm('Set them now?', false);
  let stillMissing: MissingKey[] = missing;
  if (wantsSetNow) {
    stillMissing = await runSetNowLoop(
      opts.client,
      opts.entityId,
      missing,
      opts.deps,
    );
    if (stillMissing.length === 0) return { action: 'proceed' };
    opts.deps.writer('\n');
    opts.deps.writer(
      `${stillMissing.length} key${stillMissing.length === 1 ? '' : 's'} still unset:\n`,
    );
    for (const m of stillMissing) opts.deps.writer(`   • ${m.key}\n`);
  }

  const cont = await opts.deps.confirm(
    'Continue anyway? Deploy will likely crash at startup.',
    false,
  );
  return cont ? { action: 'proceed' } : { action: 'abort' };
}

async function fetchKnownKeys(
  client: IV2Client,
  entityId: string,
): Promise<Set<string>> {
  const path = (suffix: string) =>
    `/api/v2/runtimes/${encodeURIComponent(entityId)}${suffix}`;
  // A freshly-charged entity has no namespace yet, so /env and /secrets
  // return 404. Treat that as "no known keys" — the preflight then warns on
  // every declared key, which is the correct UX. Non-404 errors still
  // propagate so we don't mask real outages.
  const [envResp, secretsResp] = await Promise.all([
    client.get<EnvListResponse>(path('/env')).catch((err) => {
      if (err instanceof V2ApiError && err.status === 404) {
        return { env: [] } satisfies EnvListResponse;
      }
      throw err;
    }),
    client.get<SecretsListResponse>(path('/secrets')).catch((err) => {
      if (err instanceof V2ApiError && err.status === 404) {
        return { secrets: [] } satisfies SecretsListResponse;
      }
      throw err;
    }),
  ]);
  const out = new Set<string>();
  for (const e of envResp.env ?? []) out.add(e.key);
  for (const s of secretsResp.secrets ?? []) out.add(s.key);
  return out;
}

function computeMissing(
  declared: ReadonlyArray<ServiceEnvKeys>,
  known: Set<string>,
): MissingKey[] {
  const out: MissingKey[] = [];
  for (const { service, keys } of declared) {
    for (const key of keys) {
      if (known.has(key)) continue;
      out.push({ service, key, classification: classifyKey(key) });
    }
  }
  return out;
}

function groupByService(
  missing: ReadonlyArray<MissingKey>,
): Map<string, MissingKey[]> {
  const m = new Map<string, MissingKey[]>();
  for (const item of missing) {
    const list = m.get(item.service) ?? [];
    list.push(item);
    m.set(item.service, list);
  }
  return m;
}

function printWarnings(
  writer: (s: string) => void,
  grouped: Map<string, MissingKey[]>,
): void {
  for (const [service, items] of grouped) {
    const n = items.length;
    writer(
      `Warning: services/${service}/.env.example declares ${n} key${n === 1 ? '' : 's'} not set on this entity:\n`,
    );
    const keyWidth = Math.max(...items.map((i) => i.key.length));
    for (const item of items) {
      const cmd =
        item.classification === 'secret'
          ? `grid secrets set ${item.key}=<value>`
          : `grid env set ${item.key}=<value>`;
      writer(`   • ${item.key.padEnd(keyWidth + 2)}${cmd}\n`);
    }
  }
}

async function runSetNowLoop(
  client: IV2Client,
  entityId: string,
  missing: ReadonlyArray<MissingKey>,
  deps: PreflightDeps,
): Promise<MissingKey[]> {
  const envSet: Record<string, string> = {};
  const secretSet: Record<string, string> = {};
  const stillMissing: MissingKey[] = [];

  for (const item of missing) {
    const hidden = item.classification === 'secret';
    const value = await deps.askInput(
      `${item.key} (leave empty to skip):`,
      { hidden },
    );
    if (value === '') {
      stillMissing.push(item);
      continue;
    }
    if (item.classification === 'secret') {
      secretSet[item.key] = value;
    } else {
      envSet[item.key] = value;
    }
  }

  const path = (suffix: string) =>
    `/api/v2/runtimes/${encodeURIComponent(entityId)}${suffix}`;
  if (Object.keys(envSet).length > 0) {
    await client.patch(path('/env'), { set: envSet });
  }
  if (Object.keys(secretSet).length > 0) {
    await client.patch(path('/secrets'), { set: secretSet });
  }

  const setN =
    Object.keys(envSet).length + Object.keys(secretSet).length;
  if (setN > 0) {
    deps.writer(`Set ${setN} key${setN === 1 ? '' : 's'}.\n`);
  }
  return stillMissing;
}
